import { useCallback, useEffect, useState } from 'react';
import { onboardingTaskService } from '../services/onboardingTaskService';
import { GroupedOnboardingTasks, OnboardingTaskStatus } from '../types';

export const useOnboardingTasks = () => {
  const [tasks, setTasks] = useState<GroupedOnboardingTasks>({ pending: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const groupedTasks = await onboardingTaskService.getTasks();
      setTasks(groupedTasks);
      setError(null);
    } catch (err) {
      setError('Error fetching tasks');
      console.error('Error fetching onboarding tasks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleTaskStatus = useCallback(async (taskId: string, currentStatus: OnboardingTaskStatus) => {
    const originalTasks = { ...tasks };
    try {
      const sourceArray = currentStatus === OnboardingTaskStatus.COMPLETED ? tasks.completed : tasks.pending;
      const destinationArray = currentStatus === OnboardingTaskStatus.COMPLETED ? tasks.pending : tasks.completed;
      const newStatus = currentStatus === OnboardingTaskStatus.COMPLETED
        ? OnboardingTaskStatus.PENDING
        : OnboardingTaskStatus.COMPLETED;

      const taskIndex = sourceArray.findIndex(t => t.id === taskId);
      if (taskIndex !== -1) {
        const updatedSource = [...sourceArray];
        const [task] = updatedSource.splice(taskIndex, 1);
        task.status = newStatus;
        task.subtasks = task.subtasks.map(s => ({ ...s, completed: newStatus === OnboardingTaskStatus.COMPLETED }));
        const updatedDestination = [...destinationArray, task].sort((a, b) => a.position - b.position);

        setTasks({
          pending: (currentStatus === OnboardingTaskStatus.COMPLETED ? updatedDestination : updatedSource).sort((a, b) => a.position - b.position),
          completed: (currentStatus === OnboardingTaskStatus.COMPLETED ? updatedSource : updatedDestination).sort((a, b) => a.position - b.position)
        });

        await onboardingTaskService.toggleTask(taskId);
      }
    } catch {
      setTasks(originalTasks);
    }
  }, [tasks]);

  const toggleSubtask = useCallback(async (taskId: string, subtaskId: string) => {
    const originalTasks = { ...tasks };
    try {
      setTasks(prevTasks => {
        const newTasks = { ...prevTasks };
        for (const status of [OnboardingTaskStatus.PENDING, OnboardingTaskStatus.COMPLETED] as const) {
          const taskIndex = newTasks[status].findIndex(t => t.id === taskId);
          if (taskIndex !== -1) {
            const task = { ...newTasks[status][taskIndex] };
            const subtaskIndex = task.subtasks.findIndex(st => st.id === subtaskId);
            if (subtaskIndex !== -1) {
              task.subtasks = [...task.subtasks];
              task.subtasks[subtaskIndex] = {
                ...task.subtasks[subtaskIndex],
                completed: !task.subtasks[subtaskIndex].completed
              };

              const allCompleted = task.subtasks.every(s => s.completed);
              const newStatus = allCompleted ? OnboardingTaskStatus.COMPLETED : OnboardingTaskStatus.PENDING;

              if (newStatus !== task.status) {
                const sourceList = [...newTasks[status]];
                sourceList.splice(taskIndex, 1);
                task.status = newStatus;
                newTasks[status] = sourceList.sort((a, b) => a.position - b.position);
                newTasks[newStatus] = [...newTasks[newStatus], task].sort((a, b) => a.position - b.position);
              } else {
                newTasks[status] = [...newTasks[status]];
                newTasks[status][taskIndex] = task;
              }
            }
            break;
          }
        }
        return newTasks;
      });

      await onboardingTaskService.toggleSubtask(taskId, subtaskId);
    } catch {
      setTasks(originalTasks);
    }
  }, [tasks]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return { tasks, loading, error, toggleTaskStatus, toggleSubtask };
};
